import { useEffect, useMemo } from 'react';
import { LuPlus } from 'react-icons/lu';
import { useParams } from 'react-router-dom';

import { HouseholdType } from '@ndvr/customer-api';

import { Button } from '@/components/ui/button';
import { Page, PageContent, PageTitle, PageTitleRow, PageTopBar } from '@/components/ui/page-layout';
import { useDockState } from '@/views/dock/dock-state';
import { getHouseholdName } from 'utils';
import { useTenantScopeLabel } from 'views/auth/tenant-selection';
import { useClientsAndProspects } from 'views/household/household-queries';

import { useListOpportunities } from './opportunity-queries';
import { OpportunityLayout, useOpportunityOverlayState, useOpportunityViewState } from './opportunity-state';
import OpportunityOverlays from './OpportunityOverlays';
import { useFilteredOpportunities } from './useFilteredOpportunities';
import { OpportunityFilter } from './widgets/OpportunityFilter';
import { OpportunityFunnelView } from './widgets/OpportunityFunnelView';
import { OpportunityKanbanView } from './widgets/OpportunityKanbanView';
import { OpportunityLayoutTabs } from './widgets/OpportunityLayoutTabs';
import { OpportunityListView } from './widgets/OpportunityListView';

export const OpportunitiesPage = () => {
    const { opportunityViewState } = useOpportunityViewState();
    const { data: allOpportunities = [], isLoading } = useListOpportunities();
    const { data: households = [] } = useClientsAndProspects();
    const { addCreateOpportunityDockItem } = useDockState();
    const tenantScopeLabel = useTenantScopeLabel();
    const { opportunityGUID } = useParams<{ opportunityGUID: string }>();
    const { openViewOpportunitySheet } = useOpportunityOverlayState();

    useEffect(() => {
        if (opportunityGUID) {
            openViewOpportunitySheet(opportunityGUID);
        }
    }, [opportunityGUID]);

    const opportunities = useFilteredOpportunities({
        opportunities: allOpportunities,
        filters: opportunityViewState,
    });
    const countText = isLoading
        ? null
        : `${opportunities.length} opportunities${tenantScopeLabel ? ` · ${tenantScopeLabel}` : ''}`;

    const householdMap = useMemo(() => {
        const map = new Map<string, { name: string; type?: HouseholdType }>();
        households.forEach((household) => {
            map.set(household.globalAccountGUID, {
                name:
                    getHouseholdName(household) ||
                    (household.householdType === HouseholdType.PROSPECT ? 'Unknown Prospect' : 'Unknown Household'),
                type: household.householdType,
            });
        });
        return map;
    }, [households]);

    return (
        <Page>
            <PageTopBar />
            <PageTitleRow
                actions={
                    <div className="flex items-center gap-2">
                        <OpportunityFilter />
                        <OpportunityLayoutTabs />
                        <Button
                            variant="outline2"
                            size="sm"
                            className="px-3"
                            onClick={() => addCreateOpportunityDockItem()}
                        >
                            <LuPlus className="h-4 w-4" /> Opportunity
                        </Button>
                    </div>
                }
            >
                <div className="flex flex-col">
                    <PageTitle>Opportunities</PageTitle>
                    {countText ? <p className="text-cocoa-600 -mt-1 text-sm font-thin">{countText}</p> : null}
                </div>
            </PageTitleRow>
            <PageContent scroll="children" padding="none">
                {opportunityViewState.layout === OpportunityLayout.LIST ? (
                    <OpportunityListView
                        opportunities={opportunities}
                        isLoading={isLoading}
                        householdMap={householdMap}
                        showHousehold
                    />
                ) : opportunityViewState.layout === OpportunityLayout.KANBAN ? (
                    <div className="min-h-0 flex-1 px-6 pb-6">
                        <OpportunityKanbanView
                            opportunities={opportunities}
                            householdMap={householdMap}
                            isLoading={isLoading}
                        />
                    </div>
                ) : (
                    <div className="min-h-0 flex-1 overflow-auto">
                        <div className="flex flex-col px-6">
                            <OpportunityFunnelView
                                opportunities={opportunities}
                                householdMap={householdMap}
                                isLoading={isLoading}
                            />
                        </div>
                    </div>
                )}
            </PageContent>

            <OpportunityOverlays />
        </Page>
    );
};
